import { useEffect, useRef, useState } from "react";
export type Page = string;
export type Overlay =
  | "categories"
  | "episodes"
  | "comments"
  | "share"
  | "auth"
  | "edit"
  | "chat"
  | "notice"
  | null;
export type Navigation = { page: Page; overlay: Overlay };
type RoutePage = { id: string; url: string; enabled: boolean };
const readHash = () => {
  try {
    const value = decodeURIComponent(location.hash.slice(1).split("?")[0]);
    return value.startsWith("layoutPreview=") ? "home" : value || "home";
  } catch {
    return "home";
  }
};
export function useNavigation(pages: RoutePage[], ready: boolean) {
  const [state, setState] = useState<Navigation>(() => ({
    page: readHash(),
    overlay: null,
  }));
  const previewQuery = useRef(
    location.hash.slice(1).startsWith("layoutPreview=")
      ? location.hash.slice(1)
      : location.hash.split("?")[1] || "",
  );
  const resolve = (value: string) =>
    pages.find(
      (p) =>
        p.enabled &&
        (p.id === value || p.url === `#${value.replace(/^#/, "")}`),
    );
  const resolved = resolve(state.page);
  const nav = resolved ? { ...state, page: resolved.id } : state;
  const url = (id: string) =>
    `${resolve(id)?.url || "#home"}${previewQuery.current ? `?${previewQuery.current}` : ""}`;
  useEffect(() => {
    const pop = (event: PopStateEvent) =>
      setState(event.state?.frameNav || { page: readHash(), overlay: null });
    const hash = () => {
      const query = location.hash.slice(1).startsWith("layoutPreview=")
        ? location.hash.slice(1)
        : location.hash.split("?")[1] || "";
      if (query !== previewQuery.current) {
        location.reload();
        return;
      }
      setState({ page: readHash(), overlay: null });
    };
    window.addEventListener("popstate", pop);
    window.addEventListener("hashchange", hash);
    return () => {
      window.removeEventListener("popstate", pop);
      window.removeEventListener("hashchange", hash);
    };
  }, []);
  useEffect(() => {
    if (!ready) return;
    const query = location.hash.slice(1).startsWith("layoutPreview=")
      ? location.hash.slice(1)
      : location.hash.split("?")[1] || "";
    if (query !== previewQuery.current) {
      location.reload();
      return;
    }
    const next = resolved
      ? { ...state, page: resolved.id }
      : { page: "home", overlay: null };
    history.replaceState(
      { frameNav: next, frameDepth: history.state?.frameDepth || 0 },
      "",
      url(next.page),
    );
    if (next.page !== state.page || next.overlay !== state.overlay)
      setState(next);
  }, [pages, ready, state.page, state.overlay]);
  function navigate(page: Page, overlay: Overlay = null) {
    const target = resolve(page);
    if (!target) return;
    const next = { page: target.id, overlay };
    if (next.page === nav.page && overlay === nav.overlay) return;
    history.pushState(
      { frameNav: next, frameDepth: (history.state?.frameDepth || 0) + 1 },
      "",
      url(target.id),
    );
    setState(next);
  }
  function replace(page: Page) {
    const target = resolve(page) || resolve("home");
    if (!target) return;
    const next = { page: target.id, overlay: null };
    history.replaceState(
      { frameNav: next, frameDepth: history.state?.frameDepth || 0 },
      "",
      url(target.id),
    );
    setState(next);
  }
  function back() {
    if ((history.state?.frameDepth || 0) > 0) history.back();
    else if (nav.overlay || nav.page !== "home") replace("home");
  }
  return { nav, navigate, replace, back };
}
